import { Box, FormField, SegmentedToggle } from '@wix/design-system';
import { LinkSmall, UploadExportSmall } from '@wix/wix-ui-icons-common';
import type { FC } from 'react';
import React, { useContext, useState } from 'react';

import {
  ModalServiceContext,
  PluginsEventsContext,
  RicosContext,
  UploadContext,
} from './../../ricos-context';
import type { VideoData } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { Helpers } from './../../ricos-types';
import { useBiNext } from './../../_shared/bi-context-provider/bi-context-provider';
import { MEDIA_POPOVERS_TABS_NAMES_BI } from './../../wix-rich-content-common';
import { Panel, PanelHeader } from './../../ui-components';

import { INSERT_VIDEO_MODAL, INSERT_VIDEO_MODAL_SOURCE_TOGGLE } from '../data-hooks';
import { EmbedVideo } from '../toolbar/embed-video';
import { UploadVideo } from '../toolbar/upload-video';
import type { VideoPluginEditorConfig } from '../types';
import { VIDEO_TYPE } from '../types';

type InsertModalProps = Pick<
  VideoPluginEditorConfig,
  'disableDownloadDefault' | 'handleFileSelection' | 'handleFileUpload'
> & {
  componentData: VideoData;
  modalId: string;
  nodeId?: string;
  enableCustomUploadOnMobile?: boolean;
  onVideoSelected: Helpers['onVideoSelected'];
};

const SourceOptionValue = {
  EMBED: 'embed',
  UPLOAD: 'upload',
} as const;

type SourceOption = (typeof SourceOptionValue)[keyof typeof SourceOptionValue];

export const InsertModal: FC<InsertModalProps> = ({
  nodeId,
  modalId,
  componentData,
  handleFileSelection,
  handleFileUpload,
  enableCustomUploadOnMobile,
  onVideoSelected,
  disableDownloadDefault,
}) => {
  const { t } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext) || {};
  const pluginsEvents = useContext(PluginsEventsContext);
  const { uploadService, updateService } = useContext(UploadContext);
  const logBi = useBiNext({ modalId, pluginId: 'video', nodeId });

  const [selectedSource, setSelectedSource] = useState<SourceOption>(
    componentData?.video?.src?.id ? 'upload' : 'embed'
  );

  const pluginEvents = {
    onPluginsPopOverTabSwitch: (data: $TSFixMe) =>
      pluginsEvents.publishPluginPopoverTabSwitch(data),
    onPluginsPopOverClick: (data: $TSFixMe) => pluginsEvents.publishPluginPopoverClick(data),
    onVideoSelected,
  };

  const closeModal = () => {
    modalService.closeModal(modalId);
  };

  const onSourceToggleClick = (_: React.SyntheticEvent, value: string) => {
    selectedSource !== value &&
      pluginEvents.onPluginsPopOverTabSwitch?.({
        pluginId: VIDEO_TYPE,
        buttonName: getTabsBiButtonName(value as SourceOption),
      });
    setSelectedSource(value as SourceOption);
    logBi({ dataHook: INSERT_VIDEO_MODAL_SOURCE_TOGGLE, value });
  };

  return (
    <Panel onCloseButtonClick={closeModal} dataHook={INSERT_VIDEO_MODAL} height="100%">
      <PanelHeader title={t('Video_Modal_Header_Title')} />

      <Box direction="vertical" padding="SP3" gap="SP3">
        <FormField label={t('Audio_Modal_Toggle_Source_Label')}>
          <SegmentedToggle
            defaultSelected={selectedSource}
            size="small"
            onClick={onSourceToggleClick}
            dataHook={INSERT_VIDEO_MODAL_SOURCE_TOGGLE}
          >
            <SegmentedToggle.Button value={SourceOptionValue.EMBED} prefixIcon={<LinkSmall />}>
              {t('VideoModal_Tabs_Embed')}
            </SegmentedToggle.Button>
            <SegmentedToggle.Button
              value={SourceOptionValue.UPLOAD}
              prefixIcon={<UploadExportSmall />}
            >
              {t('VideoModal_Tabs_Upload')}
            </SegmentedToggle.Button>
          </SegmentedToggle>
        </FormField>
        {selectedSource === 'embed' && (
          <EmbedVideo
            componentData={componentData}
            helpers={pluginEvents}
            closeModal={closeModal}
            logBi={logBi}
          />
        )}
        {selectedSource === 'upload' && (
          <UploadVideo
            nodeId={nodeId}
            helpers={pluginEvents}
            enableCustomUploadOnMobile={enableCustomUploadOnMobile}
            uploadService={uploadService}
            updateService={updateService}
            closeModal={closeModal}
            disableDownloadDefault={disableDownloadDefault}
            handleFileSelection={handleFileSelection}
            handleFileUpload={handleFileUpload}
            logBi={logBi}
          />
        )}
      </Box>
    </Panel>
  );
};

const getTabsBiButtonName = (source: SourceOption) =>
  source === 'embed' ? MEDIA_POPOVERS_TABS_NAMES_BI.embed : MEDIA_POPOVERS_TABS_NAMES_BI.upload;
